import { Box, Button, Grid, MenuItem, Stack, Typography } from "@mui/material";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import { useEffect, useMemo, useState } from "react";

import { COUNTRIES } from "../constants/countries";
import { BillingAddress } from "../types/entities";
import { AddressAutocomplete } from "./address-autocomplete";
import { FormTextField } from "./form-text-field";

export type BillingAddressDraft = Omit<BillingAddress, "id">;

const EMPTY_ADDRESS: BillingAddressDraft = {
  companyName: "",
  vatId: "",
  line1: "",
  line2: "",
  postalCode: "",
  city: "",
  country: "",
};

export interface BillingAddressFormProps {
  /** Existing saved address (read-only card mode) — null/undefined = missing. */
  address?: BillingAddress | null;
  /** Whether the form is forced into edit mode (e.g. address missing). */
  forceEdit?: boolean;
  /** Called whenever the form's draft changes. */
  onChange: (draft: BillingAddressDraft) => void;
  /** Called whenever the validity of the draft changes. */
  onValidityChange: (valid: boolean) => void;
  language: SupportedLanguage;
}

const REQUIRED_FIELDS: (keyof BillingAddressDraft)[] = [
  "companyName",
  "line1",
  "postalCode",
  "city",
  "country",
];

function isValid(draft: BillingAddressDraft): boolean {
  return REQUIRED_FIELDS.every(
    (key) => typeof draft[key] === "string" && draft[key]!.trim().length > 0
  );
}

export function BillingAddressForm({
  address,
  forceEdit,
  onChange,
  onValidityChange,
  language,
}: BillingAddressFormProps) {
  const initial: BillingAddressDraft = useMemo(
    () =>
      address
        ? {
            companyName: address.companyName,
            vatId: address.vatId ?? "",
            line1: address.line1,
            line2: address.line2 ?? "",
            postalCode: address.postalCode,
            city: address.city,
            country: address.country,
          }
        : EMPTY_ADDRESS,
    [address]
  );

  const [prevInitial, setPrevInitial] = useState(initial);
  const [draft, setDraft] = useState<BillingAddressDraft>(initial);
  const [editing, setEditing] = useState<boolean>(forceEdit ?? !address);

  if (prevInitial !== initial) {
    setPrevInitial(initial);
    setDraft(initial);
    setEditing(forceEdit ?? !address);
  }

  useEffect(() => {
    onChange(draft);
    onValidityChange(isValid(draft));
  }, [draft, onChange, onValidityChange]);

  const handleField =
    (field: keyof BillingAddressDraft) =>
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setDraft((prev) => ({ ...prev, [field]: event.target.value }));

  if (!editing && address) {
    return (
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          p: 2,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="subtitle2">
            {getLocalizedMessage(
              language,
              "changePlanModal.billingAddress.title"
            )}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            {address.companyName}
            <br />
            {address.line1}
            {address.line2 ? `, ${address.line2}` : ""}
            <br />
            {address.postalCode} {address.city}
            <br />
            {COUNTRIES.find((c) => c.value === address.country)?.label ??
              address.country}
            {address.vatId ? (
              <>
                <br />
                VAT ID: {address.vatId}
              </>
            ) : null}
          </Typography>
        </Box>
        <Button size="small" onClick={() => setEditing(true)}>
          {getLocalizedMessage(language, "dialogActions.edit")}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        p: 2,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2">
          {getLocalizedMessage(
            language,
            "changePlanModal.billingAddress.title"
          )}
        </Typography>
        {address && !forceEdit ? (
          <Button size="small" onClick={() => setEditing(false)}>
            {getLocalizedMessage(language, "dialogActions.cancel")}
          </Button>
        ) : null}
      </Stack>
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} sm={8}>
          <FormTextField
            required
            label={getLocalizedMessage(
              language,
              "changePlanModal.billingAddress.companyName"
            )}
            value={draft.companyName}
            onChange={handleField("companyName")}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormTextField
            label={getLocalizedMessage(
              language,
              "changePlanModal.billingAddress.vatId"
            )}
            value={draft.vatId ?? ""}
            onChange={handleField("vatId")}
          />
        </Grid>
        <Grid item xs={12}>
          <AddressAutocomplete
            required
            label={getLocalizedMessage(
              language,
              "changePlanModal.billingAddress.line1"
            )}
            value={draft.line1}
            onChange={(value) =>
              setDraft((prev) => ({ ...prev, line1: value }))
            }
            onSelect={(fill) =>
              setDraft((prev) => ({
                ...prev,
                line1: fill.line1,
                postalCode: fill.postalCode || prev.postalCode,
                city: fill.city || prev.city,
                country: fill.country || prev.country,
              }))
            }
          />
        </Grid>
        <Grid item xs={12}>
          <FormTextField
            label={getLocalizedMessage(
              language,
              "changePlanModal.billingAddress.line2"
            )}
            value={draft.line2 ?? ""}
            onChange={handleField("line2")}
          />
        </Grid>
        <Grid item xs={4}>
          <FormTextField
            required
            label={getLocalizedMessage(
              language,
              "changePlanModal.billingAddress.postalCode"
            )}
            value={draft.postalCode}
            onChange={handleField("postalCode")}
          />
        </Grid>
        <Grid item xs={8}>
          <FormTextField
            required
            label={getLocalizedMessage(
              language,
              "changePlanModal.billingAddress.city"
            )}
            value={draft.city}
            onChange={handleField("city")}
          />
        </Grid>
        <Grid item xs={12}>
          <FormTextField
            required
            select
            label={getLocalizedMessage(
              language,
              "changePlanModal.billingAddress.country"
            )}
            value={draft.country}
            onChange={handleField("country")}
          >
            {COUNTRIES.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </FormTextField>
        </Grid>
      </Grid>
    </Box>
  );
}
